// A deterministic score and audible-voice scheduler, independent of Web Audio.
export const AUDIO_MIX_DURATION = 16;
export const AUDIO_MIX_BPM = 120;
const BAR = 240 / AUDIO_MIX_BPM;
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const finite = (value, fallback) => Number.isFinite(value) ? value : fallback;
const STATES = ['calm', 'alert', 'combat'];
export const AUDIO_MIX_SCORE = Object.freeze([
  { id: 'room', channel: 'ambience', start: 0, duration: 16, priority: 10, frequency: 82 },
  { id: 'steps-a', channel: 'steps', start: 1, duration: 5, priority: 25, frequency: 150 },
  { id: 'impact-a', channel: 'impact', start: 3.8, duration: 1.15, priority: 65, frequency: 72 },
  { id: 'dialogue-a', channel: 'dialogue', start: 3.5, duration: 2.8, priority: 85, frequency: 330 },
  { id: 'warning-a', channel: 'warning', start: 4.1, duration: 1.6, priority: 100, frequency: 660 },
  { id: 'steps-b', channel: 'steps', start: 7.2, duration: 4.8, priority: 25, frequency: 150 },
  { id: 'impact-b', channel: 'impact', start: 8, duration: 1.1, priority: 65, frequency: 72 },
  { id: 'warning-b', channel: 'warning', start: 8.15, duration: 1.4, priority: 100, frequency: 660 },
  { id: 'dialogue-b', channel: 'dialogue', start: 11.2, duration: 2.6, priority: 85, frequency: 330 },
  { id: 'impact-c', channel: 'impact', start: 12.1, duration: 1.15, priority: 65, frequency: 72 },
].map(event => Object.freeze(event)));

export function selectAudioVoices(events, budget) {
  return [...events].sort((a, b) => b.priority - a.priority || a.start - b.start || a.id.localeCompare(b.id))
    .slice(0, clamp(Math.floor(finite(budget, 3)), 0, 5));
}

export function queueMusicTransition(schedule, state, time, quantized = true) {
  if (!STATES.includes(state) || !Number.isFinite(time)) return schedule.map(item => ({ ...item }));
  const now = clamp(time, 0, AUDIO_MIX_DURATION);
  const at = quantized ? Math.ceil((now - 1e-9) / BAR) * BAR : now;
  return [...schedule.filter(item => item.time < now || item.time > at), { time: Math.max(0, at), state }]
    .sort((a, b) => a.time - b.time);
}

export function musicStateAt(schedule, time) {
  return [...schedule].reverse().find(item => item.time <= time)?.state || 'calm';
}

function configOf(config = {}) {
  return { budget: clamp(Math.round(finite(config.budget, 3)), 0, 5), duck: clamp(finite(config.duck, .65), 0, 1), quantized: config.quantized !== false };
}

function initialSchedule(quantized) {
  return [{ time: 0, state: 'calm' }, { time: quantized ? 4 : 3.25, state: 'combat' }, { time: quantized ? 10 : 9.25, state: 'calm' }];
}

export class AudioMixModel {
  constructor(config = {}) { this.config = configOf(config); this.reset(); }
  reset() { this.time = 0; this.schedule = initialSchedule(this.config.quantized); return this.frame; }
  seek(time) { this.time = clamp(finite(time, this.time), 0, AUDIO_MIX_DURATION); return this.frame; }
  advance(dt) { return this.seek(this.time + Math.max(0, finite(dt, 0))); }
  requestMusic(state) { this.schedule = queueMusicTransition(this.schedule, state, this.time, this.config.quantized); return this.frame; }
  get recording() { return { version: 1, config: { ...this.config }, schedule: this.schedule.map(item => ({ ...item })) }; }
  loadRecording(recording) {
    if (recording?.version !== 1 || !Array.isArray(recording.schedule) || recording.schedule.some(item => !STATES.includes(item.state) || !Number.isFinite(item.time) || item.time < 0 || item.time > AUDIO_MIX_DURATION)) throw new Error('Invalid audio score');
    this.config = configOf(recording.config); this.schedule = recording.schedule.map(item => ({ ...item })).sort((a, b) => a.time - b.time); this.time = 0;
  }
  get frame() {
    const active = AUDIO_MIX_SCORE.filter(event => event.start <= this.time && event.start + event.duration > this.time);
    const voices = selectAudioVoices(active, this.config.budget);
    const protectedVoice = voices.some(event => event.channel === 'dialogue' || event.channel === 'warning');
    const gain = protectedVoice ? 1 - this.config.duck : 1;
    const state = musicStateAt(this.schedule, this.time), ended = this.time >= AUDIO_MIX_DURATION;
    return {
      time: this.time, active, voices, dropped: active.filter(event => !voices.includes(event)),
      environmentGain: gain, musicGain: gain, musicState: state,
      layers: ended ? [] : ['base', ...(state === 'calm' ? [] : ['pulse']), ...(state === 'combat' ? ['rhythm'] : [])],
      pending: this.schedule.find(item => item.time > this.time) || null,
      bar: Math.floor(this.time / BAR) + 1, beat: Math.floor(this.time / .5) % 4 + 1, ended,
    };
  }
}
